import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Music2, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/site";
import { TrustBadges } from "@/components/CertificateProvider";

const socialIcon = (icon) => {
  const map = { instagram: Instagram, facebook: Facebook, twitter: Twitter, music: Music2 };
  const Icon = map[icon] || Instagram;
  return <Icon size={18} />;
};

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-brand-greenDark text-white">
      <div className="container-px mx-auto max-w-[1400px] py-20 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="text-5xl sm:text-6xl font-bold tracking-tight leading-none">
              Faf's
            </h2>
            <p className="mt-1 text-sm tracking-[0.3em] uppercase text-white/60">
              by Rivella Enterprise
            </p>
            <p className="mt-6 max-w-md text-white/70 leading-relaxed">
              Premium, natural food and beverages crafted in Ghana — nourishing
              families, corporates and celebrations across the nation.
            </p>
            {CONTACT.socials.length > 0 && (
              <div className="mt-7 flex gap-3">
                {CONTACT.socials.map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${s.name.toLowerCase()}`}
                    aria-label={s.name}
                    className="grid h-11 w-11 place-items-center rounded-full border border-white/20 transition-all duration-300 hover:bg-white hover:text-brand-greenDark"
                  >
                    {socialIcon(s.icon)}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            <h4 className="eyebrow text-white/50 mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-white/75 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="eyebrow text-white/50 mb-5">Get in touch</h4>
            <ul className="space-y-4">
              <li>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-3 text-white/75 hover:text-white transition-colors">
                  <MessageCircle size={18} className="shrink-0" /> {CONTACT.whatsapp}
                </a>
              </li>
              <li>
                <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 text-white/75 hover:text-white transition-colors">
                  <Phone size={18} className="shrink-0" /> {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={CONTACT.phone2Href} data-testid="footer-phone-2" className="flex items-center gap-3 text-white/75 hover:text-white transition-colors">
                  <Phone size={18} className="shrink-0" /> {CONTACT.phone2}
                </a>
              </li>
              <li>
                <a href={CONTACT.emailHref} data-testid="footer-email" className="flex items-center gap-3 text-white/75 hover:text-white transition-colors">
                  <Mail size={18} className="shrink-0" /> {CONTACT.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/75">
                <MapPin size={18} className="shrink-0" /> {CONTACT.address}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-8">
          <p className="eyebrow text-white/40 mb-4">Certified & Trusted</p>
          <TrustBadges variant="dark" />
        </div>

        <div className="mt-10 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <p>© {new Date().getFullYear()} Rivella Enterprise. All rights reserved.</p>
          <p>Crafted with care in Ghana 🇬🇭</p>
        </div>
      </div>
    </footer>
  );
};
